import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Youtube, Image as ImageIcon, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImageUpload } from './ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'satsang', label: 'Satsang' },
  { value: 'mahayaga', label: 'Mahayaga' },
  { value: 'mantra_japa', label: 'Mantra Japa' },
  { value: 'event', label: 'Event' },
];

const emptyForm = () => ({
  title: '',
  media_type: 'photo',
  media_url: '',
  thumbnail_url: '',
  category: 'general',
  state_code: 'ALL',
  is_published: true,
});

export const GalleryManagement = () => {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [g, s, m] = await Promise.all([
        axios.get(`${API_URL}/api/gallery/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/states`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/me`, { withCredentials: true }),
      ]);
      setItems(g.data);
      setStates(s.data.states || []);
      setMe(m.data);
    } catch (err) {
      toast.error('Failed to load gallery items');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    const initial = emptyForm();
    if (me?.role === 'state_admin' && me.state_code !== 'ALL') {
      initial.state_code = me.state_code;
    }
    setForm(initial);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      media_type: item.media_type,
      media_url: item.media_url,
      thumbnail_url: item.thumbnail_url || '',
      category: item.category || 'general',
      state_code: item.state_code || 'ALL',
      is_published: item.is_published !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.media_url?.trim()) {
      toast.error(form.media_type === 'youtube' ? 'Paste a YouTube URL' : 'Upload a media file');
      return;
    }
    try {
      const payload = { ...form };
      if (editing) {
        await axios.put(`${API_URL}/api/gallery/${editing.id}`, payload, { withCredentials: true });
        toast.success('Gallery item updated');
      } else {
        await axios.post(`${API_URL}/api/gallery`, payload, { withCredentials: true });
        toast.success('Gallery item added');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/gallery/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const stateName = (code) => states.find((s) => s.code === code)?.name || code;
  const isSuperAdmin = me?.role === 'super_admin';
  // State admin restricted to their state + ALL
  const availableStates = isSuperAdmin
    ? states
    : states.filter((s) => s.code === 'ALL' || s.code === me?.state_code);

  const mediaIcon = (t) => (t === 'youtube' ? <Youtube size={14} /> : t === 'video_file' ? <Video size={14} /> : <ImageIcon size={14} />);

  return (
    <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6" data-testid="gallery-management">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading text-charcoal-800 flex items-center gap-2">
            <ImageIcon className="text-saffron-600" size={22} /> Gallery Media
          </h2>
          <p className="text-sm text-charcoal-500 mt-1">
            Upload a photo or paste a YouTube link, tag it to a state, and it will appear in the public Gallery filtered by region.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="gallery-add-button"
          className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-4 py-2 hover:bg-saffron-500 transition-colors self-start sm:self-auto"
        >
          <Plus size={18} /> Add Media
        </button>
      </div>

      {loading ? (
        <p className="text-center text-charcoal-500 py-12">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-center text-charcoal-500 py-12">No gallery media yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <div key={item.id} className="border border-charcoal-100 rounded-xl overflow-hidden bg-cream/30 group">
              <div className="relative aspect-video bg-charcoal-100">
                {item.thumbnail_url || item.media_type === 'photo' ? (
                  <img
                    src={item.thumbnail_url || item.media_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-charcoal-400">
                    {mediaIcon(item.media_type)}
                  </div>
                )}
                <div className="absolute top-2 left-2 bg-charcoal-900/80 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
                  {mediaIcon(item.media_type)}
                  <span className="uppercase tracking-wider">{item.media_type.replace('_', ' ')}</span>
                </div>
                {!item.is_published && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] px-2 py-1 rounded-full">
                    Hidden
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-body font-semibold text-charcoal-800 line-clamp-1">{item.title}</h3>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="text-[10px] uppercase tracking-wider bg-saffron-50 text-saffron-700 px-2 py-0.5 rounded-full">
                    {item.category || 'general'}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider bg-gold-50 text-gold-700 px-2 py-0.5 rounded-full">
                    {stateName(item.state_code)}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => openEdit(item)}
                    data-testid={`gallery-edit-${item.id}`}
                    className="text-saffron-600 hover:text-saffron-500"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => remove(item)}
                    data-testid={`gallery-delete-${item.id}`}
                    className="text-red-600 hover:text-red-500"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b border-charcoal-100 sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-heading text-charcoal-800">
                {editing ? 'Edit Gallery Item' : 'Add Gallery Item'}
              </h3>
              <button onClick={closeModal} className="text-charcoal-500 hover:text-charcoal-700" data-testid="gallery-modal-close">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Title *"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                data-testid="gallery-form-title"
              />

              <div>
                <label className="block text-xs font-body font-semibold text-charcoal-600 mb-2 uppercase tracking-wider">Media Source</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, media_type: 'photo', media_url: '', thumbnail_url: '' })}
                    className={`rounded-lg p-3 border text-sm flex flex-col items-center gap-1 transition-colors ${
                      form.media_type === 'photo'
                        ? 'bg-saffron-600 text-white border-saffron-600'
                        : 'bg-white text-charcoal-700 border-charcoal-200 hover:border-saffron-400'
                    }`}
                    data-testid="gallery-type-photo"
                  >
                    <ImageIcon size={18} />
                    <span className="font-semibold">Photo Upload</span>
                    <span className={`text-[10px] ${form.media_type === 'photo' ? 'text-white/80' : 'text-charcoal-500'}`}>
                      Upload a JPG/PNG/WEBP
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, media_type: 'youtube', media_url: '', thumbnail_url: '' })}
                    className={`rounded-lg p-3 border text-sm flex flex-col items-center gap-1 transition-colors ${
                      form.media_type === 'youtube'
                        ? 'bg-saffron-600 text-white border-saffron-600'
                        : 'bg-white text-charcoal-700 border-charcoal-200 hover:border-saffron-400'
                    }`}
                    data-testid="gallery-type-youtube"
                  >
                    <Youtube size={18} />
                    <span className="font-semibold">YouTube Link</span>
                    <span className={`text-[10px] ${form.media_type === 'youtube' ? 'text-white/80' : 'text-charcoal-500'}`}>
                      Paste a YouTube URL
                    </span>
                  </button>
                </div>
              </div>

              {form.media_type === 'photo' ? (
                <ImageUpload
                  label="Photo *"
                  recommendedSize="1600×900 (16:9)"
                  value={form.media_url}
                  onChange={(v) => setForm({ ...form, media_url: v })}
                  testId="gallery-photo"
                />
              ) : (
                <div>
                  <label className="block text-sm font-body font-semibold text-charcoal-700 mb-1.5">YouTube URL *</label>
                  <input
                    type="url"
                    required
                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                    value={form.media_url}
                    onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    data-testid="gallery-youtube-url"
                  />
                  <p className="text-[11px] text-charcoal-500 italic mt-1">
                    Thumbnail is auto-fetched from YouTube. You can override with a custom image below.
                  </p>
                </div>
              )}

              {form.media_type === 'youtube' && (
                <ImageUpload
                  label="Custom Thumbnail (optional)"
                  recommendedSize="1280×720 (16:9)"
                  value={form.thumbnail_url}
                  onChange={(v) => setForm({ ...form, thumbnail_url: v })}
                  testId="gallery-custom-thumb"
                />
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-body font-semibold text-charcoal-600 mb-1.5 uppercase tracking-wider">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    data-testid="gallery-form-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-body font-semibold text-charcoal-600 mb-1.5 uppercase tracking-wider">State Scope</label>
                  <select
                    value={form.state_code}
                    onChange={(e) => setForm({ ...form, state_code: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    data-testid="gallery-form-state"
                  >
                    {availableStates.map((s) => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-charcoal-700">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  className="accent-saffron-600"
                  data-testid="gallery-form-published"
                />
                <span>Published (visible on the public Gallery)</span>
              </label>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-charcoal-200 text-charcoal-700 rounded-lg px-6 py-3 hover:bg-charcoal-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-saffron-600 text-white rounded-lg px-6 py-3 hover:bg-saffron-500 transition-colors"
                  data-testid="gallery-form-submit"
                >
                  {editing ? 'Save Changes' : 'Add to Gallery'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
